if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.loadingSpinner = this.querySelector('.loading__spinner');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        if (this.loadingSpinner) this.loadingSpinner.classList.remove('hidden');

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const taxItems = this.getTaxItemsFromForm(formData);

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        let resolvedTaxItems = [];
        if (taxItems.length > 0) {
          resolvedTaxItems = await this.resolveTaxItems(taxItems);
        }

        const addPromise =
          resolvedTaxItems.length > 0
            ? this.addWithTaxItems(formData, resolvedTaxItems)
            : this.addMainProductOnly(formData);

        addPromise
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            return response;
          })
          .then((finalResponse) => {
            if (!finalResponse) return;

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: finalResponse,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(finalResponse);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(finalResponse);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            if (this.loadingSpinner) this.loadingSpinner.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      getTaxItemsFromForm(formData) {
        const items = [];
        for (let i = 0; ; i++) {
          const raw = formData.get(`tax_item[${i}]`);
          if (!raw) break;
          const cost = formData.get(`tax_cost[${i}]`);
          items.push({ raw, cost: cost || '' });
        }
        if (items.length === 0) {
          const singleRaw = formData.get('tax_item');
          if (singleRaw) {
            items.push({ raw: singleRaw, cost: formData.get('tax_cost') || '' });
          }
        }
        return items;
      }

      async resolveTaxItems(taxItems) {
        const tasks = taxItems.map(async (tax) => {
          const value = tax.raw;
          if (!value) return null;

          // If it's a pure numeric value, treat it as a variant id directly.
          if (/^\d+$/.test(value)) {
            return { id: value, cost: tax.cost || '' };
          }

          // Otherwise treat as a product handle and resolve to the first variant id.
          const variantId = await this.getVariantIdForHandle(value);
          if (!variantId) return null;

          return { id: variantId, cost: tax.cost || '' };
        });

        const resolved = await Promise.all(tasks);
        return resolved.filter((item) => !!item);
      }

      async getVariantIdForHandle(handle) {
        if (!handle) return null;

        if (!this.taxVariantCache) {
          this.taxVariantCache = {};
        }

        if (this.taxVariantCache[handle]) {
          return this.taxVariantCache[handle];
        }

        try {
          const response = await fetch(`${window.location.origin}/products/${handle}.json`);
          if (!response.ok) return null;

          const data = await response.json();
          const variant = data?.product?.variants?.[0];

          if (variant?.id) {
            this.taxVariantCache[handle] = variant.id;
            return variant.id;
          }
        } catch (e) {
          console.error('Failed to fetch tax product JSON for handle:', handle, e);
        }

        return null;
      }

      addWithTaxItems(formData, taxItems) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const quantity = formData.get('quantity') || 1;
        const parentVariantId = formData.get('id');

        const nestedFormData = new FormData();
        nestedFormData.append('items[0][id]', parentVariantId);
        nestedFormData.append('items[0][quantity]', quantity);
        nestedFormData.append('items[0][properties][_pk_tax_items]', JSON.stringify(this.buildTaxMetadata(taxItems)));

        taxItems.forEach((tax, index) => {
          const idx = index + 1;
          nestedFormData.append(`items[${idx}][id]`, tax.id);
          nestedFormData.append(`items[${idx}][quantity]`, quantity);
          nestedFormData.append(`items[${idx}][parent_id]`, parentVariantId);
          if (tax.cost) {
            nestedFormData.append(`items[${idx}][properties][_cost]`, tax.cost);
          }
        });

        this.appendCartSections(nestedFormData);

        config.body = nestedFormData;
        return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
      }

      buildTaxMetadata(taxItems) {
        return taxItems
          .filter((tax) => tax && tax.id)
          .map((tax) => ({
            id: String(tax.id),
            cost: tax.cost || '',
          }));
      }

      appendCartSections(body) {
        // Notification ATC paints the popup from the add JSON. Rendering those
        // sections against the collection page is what holds /cart/add.js.
        if (!this.cart || this.cart.tagName === 'CART-NOTIFICATION') return;
        if (typeof this.cart.getSectionsToRender !== 'function') return;

        body.append(
          'sections',
          this.cart.getSectionsToRender().map((section) => section.id)
        );
        body.append('sections_url', window.location.pathname);
      }

      addMainProductOnly(formData) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        this.appendCartSections(formData);
        config.body = formData;
        return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
      }
    }
  );
}
